import React from "react";
import { Box, CircularProgress } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import { ediphiPalette } from "../../styles/colors";
import { CardButton } from "./CardButton.styled";
import { HeaderText } from "../text/HeaderText.styled";
import { BodyText } from "../text/BodyText.styled";
import PaneTooltip, { INFO_TOOLTIP_DELAY_MS } from "../text/PaneTooltip";

interface CardActionButtonProps {
  label: string;
  /** Secondary line under the label, e.g. the sheet an action would run against. */
  subLabel?: string;
  onClick: () => void;
  disabled?: boolean;
  /** Shows a spinner in the trailing slot and blocks further clicks. */
  loading?: boolean;
  /**
   * Identifies the item, e.g. a chart type's shape. Already-rendered rather than a component
   * reference, since chartIconGetter hands back a node.
   */
  leadingIcon?: React.ReactNode;
  /** Trailing marker, e.g. the multi-sheet icon on report templates. */
  icon?: SvgIconComponent;
  iconTooltip?: string;
  /**
   * Drops the resting outline so the row reads as a card rather than a button. For page content,
   * where the surrounding items are cards; a drawer's list of choices keeps the outline.
   */
  disableOutline?: boolean;
}

const CardActionButton: React.FC<CardActionButtonProps> = ({
  label,
  subLabel,
  onClick,
  disabled,
  loading,
  leadingIcon,
  icon,
  iconTooltip,
  disableOutline,
}) => {
  const MarkerIcon = icon;

  return (
    <CardButton
      onClick={onClick}
      // The button being acted on takes the same grey ring the datasource tiles and report rows
      // get. Independent of `disabled`, so a caller can use the look without locking its siblings.
      $selected={loading}
      disabled={disabled || loading}
      disableRipple
      fullWidth
      sx={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        textTransform: "none",
        px: 2,
        backgroundColor: ediphiPalette.surface,
        "&:hover": {
          backgroundColor: ediphiPalette.surface,
        },
        // HeaderText and BodyText set their own colours, so they ignore the one the button takes
        // when disabled — without this a disabled row keeps near-black text beside its greyed-out
        // neighbours, and only the running row would look right.
        "&.Mui-disabled": {
          // Pinned here rather than left to CardButton: the acting row also takes activeStyles'
          // `background: none` at root, so its fill has to be settled at this specificity or it
          // reads lighter than the rows beside it.
          backgroundColor: ediphiPalette.surfaceSubtle,
          "& .MuiTypography-root": { color: "inherit" },
        },
        // Resting state only — the hover ring and the running row's grey ring both still apply, so
        // an outline-less row still reads as clickable and still runs like every other button.
        ...(disableOutline && { boxShadow: "none" }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
        {/* The spinner takes the leading icon's place, so it sits beside the label naming what runs. */}
        {loading ? (
          <CircularProgress size={18} sx={{ color: ediphiPalette.info600, flexShrink: 0 }} />
        ) : (
          leadingIcon && <Box sx={{ display: "flex", flexShrink: 0 }}>{leadingIcon}</Box>
        )}
        <Box sx={{ minWidth: 0, textAlign: "left" }}>
          <HeaderText>{label}</HeaderText>
          {subLabel && <BodyText noWrap>{subLabel}</BodyText>}
        </Box>
      </Box>
      {MarkerIcon &&
        (iconTooltip ? (
          <PaneTooltip title={iconTooltip} enterDelay={INFO_TOOLTIP_DELAY_MS}>
            {/* The whole card is one button, so the icon has to swallow the click or reading the
                tooltip would fire the card's action. */}
            <Box
              component="span"
              sx={{ display: "flex", flexShrink: 0 }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <MarkerIcon sx={{ fontSize: "18px" }} />
            </Box>
          </PaneTooltip>
        ) : (
          <MarkerIcon sx={{ fontSize: "18px" }} />
        ))}
    </CardButton>
  );
};

export default CardActionButton;
